import { useCallback, useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { tryParseJSON } from '@adv-frontend/yastore-modules/lib';
import { getB2BDomainByEnv, OAUTH_POPUP_POLL_INTERVAL_MS } from '#shared/lib/const';
import { windowOpenWithPrefetch } from '#shared/lib/router';
import { showError } from '#shared/lib/toast';

type OauthCookiePayload = {
    code?: string;
    state?: string;
    error?: string;
};

type UseOauthPopupConnectOptions<Credentials> = {
    cookieKey: string;
    serviceName: string;
    getCredentials: () => Promise<Credentials>;
    buildUrl: (credentials: Credentials) => string;
    getCookieDomain?: () => string;
    onCookieSuccess: (data: { code: string; state: string }) => Promise<void>;
};

type UseOauthPopupConnectResult = {
    isConnecting: boolean;
    handleConnect: () => void;
};

/**
 * Общий скелет OAuth-подключения через popup + cookie polling.
 *
 * Открывает popup, опрашивает cookie, парсит JSON и проверяет code/state (и error, если есть).
 * При успехе вызывает onCookieSuccess. Ошибки валидации/onCookieSuccess — через showError.
 * Ручное закрытие popup — молча сбрасывает isConnecting.
 *
 * @kind helper
 * @typeParam Credentials — тип credentials для OAuth URL
 * @returns {UseOauthPopupConnectResult}
 */
export function useOauthPopupConnect<Credentials>({
    cookieKey,
    serviceName,
    getCredentials,
    buildUrl,
    getCookieDomain = getB2BDomainByEnv,
    onCookieSuccess,
}: UseOauthPopupConnectOptions<Credentials>): UseOauthPopupConnectResult {
    const [isConnecting, setIsConnecting] = useState(false);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(
        () => () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        },
        []
    );

    const handleConnect = useCallback(async () => {
        if (isConnecting) {
            return;
        }

        setIsConnecting(true);

        const popup = await windowOpenWithPrefetch(
            async () => {
                const credentials = await getCredentials();

                return { url: buildUrl(credentials) };
            },
            (error) => {
                showError({ label: `Ошибка получения необходимых данных для подключения ${serviceName}` }, error);
            },
            `${serviceName} oauth`
        );

        if (!popup) {
            setIsConnecting(false);

            return;
        }

        const cookieDomain = getCookieDomain();

        const timer = setInterval(async () => {
            const cookieValue = Cookies.get(cookieKey);

            if (cookieValue) {
                Cookies.remove(cookieKey, { domain: cookieDomain, path: '/' });

                const data = tryParseJSON<OauthCookiePayload>(cookieValue);

                if (!data) {
                    return;
                }

                clearInterval(timer);
                timerRef.current = null;

                try {
                    const { code, state, error } = data;

                    if (error || !code || !state) {
                        throw new Error(error || 'Невалидная cookie');
                    }

                    await onCookieSuccess({ code, state });
                } catch (error) {
                    showError(
                        { label: `Произошла ошибка при подключении интеграции с ${serviceName}`, durationMs: 10_000 },
                        error
                    );
                } finally {
                    popup.close();
                    setIsConnecting(false);
                }

                return;
            }

            if (popup.closed) {
                clearInterval(timer);
                timerRef.current = null;
                setIsConnecting(false);
            }
        }, OAUTH_POPUP_POLL_INTERVAL_MS);

        timerRef.current = timer;
    }, [isConnecting, getCredentials, buildUrl, getCookieDomain, onCookieSuccess, serviceName, cookieKey]);

    return { isConnecting, handleConnect };
}
